import dayjs, { Dayjs } from 'dayjs'
import { FormVacationPeriod } from '../types/form/FormVacationPeriod'
import { PaidVacationPeriodModifyDTO, PaidVacationPeriodReturnDTO } from '../redux/reducers/contractSlice'

export const hasInvalidEmploymentRange = (startDate: Dayjs | null, endDate: Dayjs | null): boolean => {
  if (!startDate || !endDate) return false
  return dayjs(startDate).isAfter(dayjs(endDate))
}

export const hasInvalidVacationPeriods = (periods: FormVacationPeriod[]): boolean => {
  return periods.some(period => dayjs(period.startDate).isAfter(dayjs(period.endDate)))
}

export const hasInvalidNumberOfDays = (periods: FormVacationPeriod[]): boolean => {
  return periods.some(period => period.numberOfDays <= 0)
}

export const vacationPeriodHasInvalidNumberOfDays = (period: FormVacationPeriod): boolean => {
  const start = period.startDate ? dayjs(period.startDate) : null
  const end = period.endDate ? dayjs(period.endDate) : null

  if (!start || !end) return false
  if (start.isAfter(end, 'day') || period.numberOfDays <= 0) return true

  const availableDays = end.diff(start, 'day') + 1
  return period.numberOfDays > availableDays
}

export const vacationPeriodsHaveInvalidNumberOfDays = (periods: FormVacationPeriod[]): boolean => {
  if (!periods || periods.length === 0) return false
  return periods.some(vacationPeriodHasInvalidNumberOfDays)
}

export const periodsOverlap = (periods?: FormVacationPeriod[]) => {
  if (!periods || periods.length <= 1) return false

  const normalized = periods
    .map(p => {
      const start = p.startDate ? dayjs(p.startDate) : null
      const end = p.endDate ? dayjs(p.endDate) : null
      return { start, end }
    })
    .filter(p => p.start && p.end) as { start: Dayjs; end: Dayjs }[]

  for (let i = 0; i < normalized.length; i++) {
    for (let j = i + 1; j < normalized.length; j++) {
      const a = normalized[i]
      const b = normalized[j]
      if (!(a.end.isBefore(b.start, 'day') || b.end.isBefore(a.start, 'day'))) {
        return true
      }
    }
  }

  return false
}

export const periodsOutsideContract = (
  periods?: FormVacationPeriod[],
  contractStart?: Dayjs | null,
  contractEnd?: Dayjs | null
) => {
  if (!periods || periods.length === 0) return false
  if (!contractStart || !contractEnd) return false

  const normalized = periods
    .map(p => ({
      start: p.startDate ? dayjs(p.startDate) : null,
      end: p.endDate ? dayjs(p.endDate) : null,
    }))
    .filter(p => p.start && p.end) as { start: Dayjs; end: Dayjs }[]

  for (let i = 0; i < normalized.length; i++) {
    const p = normalized[i]
    if (p.start.isBefore(dayjs(contractStart), 'day') || p.end.isAfter(dayjs(contractEnd), 'day')) {
      return true
    }
  }

  return false
}

export const mapVacationPeriodsToFormValues = (periods: PaidVacationPeriodReturnDTO[]): FormVacationPeriod[] => {
  return periods.map(p => ({
    id: p.id,
    numberOfDays: p.vacationDays,
    startDate: p.startDate ? dayjs(p.startDate).startOf('day') : null,
    endDate: p.endDate ? dayjs(p.endDate).startOf('day') : null,
    overtime: p.overtimeHours,
  }))
}

export const mapFormVacationPeriodsToModifyDTO = (periods: FormVacationPeriod[]): PaidVacationPeriodModifyDTO[] => {
  return periods.map(v => {
    const start = v.startDate ? v.startDate.startOf('day').utc(true).toISOString() : ''
    const end = v.endDate ? v.endDate.startOf('day').utc(true).toISOString() : ''

    return {
      id: v.id,
      overtimeHours: Number(v.overtime) || 0,
      vacationDays: Number(v.numberOfDays) || 0,
      version: null,
      startDate: start,
      endDate: end,
    }
  })
}

export const periodOverlapsWithOthers = (
  currentPeriod: { startDate: Dayjs | null; endDate: Dayjs | null },
  allPeriods: FormVacationPeriod[],
  currentIndex: number
): boolean => {
  const start = currentPeriod.startDate ? dayjs(currentPeriod.startDate) : null
  const end = currentPeriod.endDate ? dayjs(currentPeriod.endDate) : null

  if (!start || !end) return false

  for (let i = 0; i < allPeriods.length; i++) {
    if (i === currentIndex) continue

    const otherStart = allPeriods[i].startDate ? dayjs(allPeriods[i].startDate) : null
    const otherEnd = allPeriods[i].endDate ? dayjs(allPeriods[i].endDate) : null

    if (!otherStart || !otherEnd) continue

    if (!(end.isBefore(otherStart, 'day') || otherEnd.isBefore(start, 'day'))) {
      return true
    }
  }

  return false
}

export const isDateWithinContract = (
  date: Dayjs | null,
  contractStart: Dayjs | null,
  contractEnd: Dayjs | null
): boolean => {
  if (!date || !contractStart || !contractEnd) return true
  return dayjs(date).isBetween(contractStart, contractEnd, 'day', '[]')
}

export const isValidDateRange = (startDate: Dayjs | null, endDate: Dayjs | null): boolean => {
  if (!startDate || !endDate) return true
  return dayjs(startDate).isBefore(dayjs(endDate), 'day')
}
